import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

import { getIsLegacyWordFile, guestSessionHeaderName, Select } from '@aline/common';
import { params, routes } from '@aline/routes';
import { useApolloClient } from '@apollo/client/react';
import { useDroppable } from '@dnd-kit/core';
import type { Core } from '@pdftron/webviewer';
import clsx from 'clsx';
import mime from 'mime-types';
import { getGuestSessionToken } from 'src/auth';
import { setVariablePanelStateForTabId } from 'src/components/variables';
import { useRegisterSearchListeners } from 'src/components/viewer/hooks';
import { useMaybeDocumentViewer } from 'src/components/viewer/viewer-context';
import {
  useRegisterAnnotationListeners,
  useRegisterDragAnnotationListeners,
  useRegisterFocusedAnnotationListener,
  useResizeCursorOverride,
  useZoomLevel,
} from 'src/components/viewer/viewer-hooks';
import { getClientPublicOrigin, pdfTronKey } from 'src/config';
import { useRequiredEditorRuntime } from 'src/editor/editor-runtime-registry';
import {
  useEditorTabStateActionsForTabId,
  useEditorTabStateSelectorForTabId,
} from 'src/editor/editor-tab-state-registry';
import { useWalkthroughActive } from 'src/editor/toolbars/editor-app-bars/helpers';
import { useDrawerSearchParam } from 'src/editor/toolbars/right-panel/drawer-search-params';
import { Apollo } from 'src/gql/generated';
import { dropIds, useDocumentParams } from 'src/helpers';
import { composeRefs } from 'src/helpers/compose-refs';
import { IS_MOBILE } from 'src/helpers/environment';
import {
  createDocFileUrlRefresher,
  fetchFreshFilestackBlob,
} from 'src/helpers/integrations/filestack-download';
import {
  useAlineMeasure,
  useCleanupOrphanCannedFieldsInPdf,
  useDocumentSignatureStatus,
  useGetCurrentDoc,
  useNotificationDispatch,
  useSyncCannedFieldValuesToPdf,
} from 'src/hooks';
import { useSubscribeToAnnotationChanges } from 'src/hooks/annotations';
import { useGetEditorUserState } from 'src/hooks/editor-user-state';
import { DocumentOutline } from 'src/layout-engine/components/document-outline';
import { EditorLoadingPage } from 'src/layout-engine/components/EditorLoadingPage';
import { NotificationsActionType } from 'src/notifications';
import { Sentry } from 'src/sentry';
import type { ReactSetStateTuple } from 'src/types';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';
import { z } from 'zod';

import { stripPdfOwnWidgetAnnots } from './helpers';
import { PdfFindPopper } from './PdfFindPopper';
import { ComponentLifecycleCallbacks } from '../generic/ComponentLifecycleCallbacks';
import { useScrollable } from '../generic/scrollable';

const useStyles = makeStyles()(theme => ({
  loadingOverlay: {
    inset: 0,
    position: 'absolute',
    zIndex: 1,
  },
  scrollView: {
    backgroundColor: theme.appColors.background,
    height: '100%',
    position: 'relative',
    width: '100%',
  },
  viewer: {
    /**
     * iOS Safari auto-zooms the viewport when focusing any form control whose
     * computed font-size is < 16px. Force 16px on inputs/selects/textareas
     * inside the viewer to prevent that.
     */
    [theme.breakpoints.down('sm')]: {
      '& input, & select, & textarea': {
        fontSize: '16px !important',
      },
    },
    margin: 'auto',
  },
}));
export interface FileViewerVersion extends Pick<Apollo.StaticFileVersion, '__typename' | 'id'> {
  fileLocation: Pick<Apollo.StaticFileVersion['fileLocation'], '__typename' | 'id'>;
}

interface FileViewerBodyProps {
  fileVersion: FileViewerVersion;
  isSigned: boolean;
}

export function FileViewerBody({ fileVersion, isSigned }: FileViewerBodyProps): JSX.Element {
  const id = useId();
  const { classes } = useStyles();
  const { tabId } = useRequiredEditorRuntime();
  const { isInSigningCeremony } = useGetEditorUserState();
  const { walkThroughActive } = useWalkthroughActive();
  const [setRef, measurements] = useAlineMeasure<HTMLDivElement>();
  const isViewerLoading = useEditorTabStateSelectorForTabId(tabId, state => state.viewer.isLoading);
  const { setValue: setEditFieldId } = useDrawerSearchParam(params.rightPanel.editFieldId);
  const { setNodeRef } = useDroppable({
    data: { tabId },
    id: dropIds.fileViewerBody(tabId),
  });
  const {
    documentViewer,
    scrollTuple: [scrollNode, setScrollNode],
    unloadFileViewer,
    viewerTuple: [, setViewerNode],
  } = useLoadDocumentIntoViewer(fileVersion, isSigned, tabId);
  const {
    classes: scrollableClasses,
    scrollableInnerRef,
    scrollableRef,
    scrollbarElements,
  } = useScrollable({ id, visibleWhenNotOverflowing: { vertical: true } });

  const { documentId } = useDocumentParams();
  const { document: alineDocument } = useGetCurrentDoc(documentId);
  const { wasSentForSignature } = useDocumentSignatureStatus(documentId);

  useZoomLevel();
  useSyncViewerPageWidthPx(tabId);
  useSubscribeToAnnotationChanges(fileVersion.id);
  useSyncCannedFieldValuesToPdf(
    documentViewer,
    alineDocument?.workflow?.id ?? null,
    tabId,
    wasSentForSignature
  );
  useCleanupOrphanCannedFieldsInPdf(documentViewer, alineDocument?.workflow?.id ?? null);
  useRefreshDocumentViewerScrollView(documentViewer, measurements?.width ?? 0);
  useClearViewerPanelStateOnClick({ scrollNode, setEditFieldId, tabId });

  return (
    <div className={clsx(classes.scrollView, scrollableClasses.root)}>
      {isViewerLoading && (
        <div
          className={classes.loadingOverlay}
          data-cy={Select.document.staticViewer.loadingViewerProgressBar}
        >
          <EditorLoadingPage />
        </div>
      )}
      <div
        ref={composeRefs<HTMLDivElement>(setRef, setScrollNode, scrollableRef)}
        className={scrollableClasses.scrollable}
        id={id}
      >
        <div ref={scrollableInnerRef} className={scrollableClasses.scrollableInner}>
          {!(IS_MOBILE && (isInSigningCeremony || walkThroughActive)) && (
            <DocumentOutline areHeadingsSupported={false} />
          )}
          <PdfFindPopper />
          <div
            ref={composeRefs<HTMLDivElement>(setViewerNode, setNodeRef)}
            className={classes.viewer}
            data-cy={Select.document.staticViewer.viewerEl}
          />
        </div>
      </div>
      {scrollbarElements}
      <ComponentLifecycleCallbacks onUnmount={unloadFileViewer} />
    </div>
  );
}

interface UseLoadDocumentIntoViewer {
  documentViewer: Core.DocumentViewer | null;
  scrollTuple: ReactSetStateTuple<HTMLDivElement | null>;
  unloadFileViewer: () => void;
  viewerTuple: ReactSetStateTuple<HTMLDivElement | null>;
}

function useLoadDocumentIntoViewer(
  fileVersion: FileViewerVersion,
  isSigned: boolean,
  tabId: UUID
): UseLoadDocumentIntoViewer {
  const client = useApolloClient();
  const dispatch = useNotificationDispatch();
  const { documentId } = useDocumentParams();
  const documentViewer = useMaybeDocumentViewer();
  const {
    viewer: {
      markViewerDocumentCleared,
      markViewerDocumentLoaded,
      markViewerLoadFinished,
      markViewerLoadStarted,
    },
  } = useEditorTabStateActionsForTabId(tabId);
  const currentDocumentKeyRef = useRef<string | null>(null);
  const failedDocumentKeyRef = useRef<string | null>(null);
  const [viewerNode, setViewerNode] = useState<HTMLDivElement | null>(null);
  const [scrollNode, setScrollNode] = useState<HTMLDivElement | null>(null);
  // Use fetchPolicy: 'network-only' so we always fetch fresh XFDF.
  const [getAnnotations] = Apollo.useGetAnnotationsLazyQuery({ fetchPolicy: 'network-only' });
  const documentKey = getViewerDocumentKey(documentId, fileVersion.id);
  // Use useCallback so the func can safely be passed as a dependency to the useEffect below.
  const dispatchErrorNotification = useCallback(
    () =>
      dispatch({
        payload: {
          key: uuid4(),
          message: 'An error occurred.',
          variant: 'error',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      }),
    [dispatch]
  );

  useRegisterSearchListeners();
  useRegisterAnnotationListeners();
  useRegisterDragAnnotationListeners();
  useRegisterFocusedAnnotationListener();
  useResizeCursorOverride();
  useBindDocumentViewerElements({ documentViewer, scrollNode, viewerNode });

  const loadFileViewer = useCallback(async () => {
    if (!documentViewer || !viewerNode || !scrollNode) return;

    const currentDocumentViewer = documentViewer;
    if (currentDocumentKeyRef.current === documentKey) return;
    if (failedDocumentKeyRef.current === documentKey) return;

    const isCurrentLoad = beginViewerLoadForDocumentViewer(currentDocumentViewer);

    function handleDocumentLoaded() {
      if (!isCurrentLoad()) return cleanupLoadListener();
      markViewerDocumentLoaded();
      cleanupLoadListener();
    }

    function cleanupLoadListener() {
      currentDocumentViewer.removeEventListener('documentLoaded', handleDocumentLoaded);
    }

    function stopIfStale() {
      if (isCurrentLoad()) return false;
      cleanupLoadListener();
      return true;
    }

    function failCurrentLoad() {
      currentDocumentKeyRef.current = null;
      failedDocumentKeyRef.current = documentKey;
      markViewerDocumentCleared();
      cleanupLoadListener();
    }

    if (currentDocumentKeyRef.current) {
      currentDocumentViewer.closeDocument();
      markViewerDocumentCleared();
    }

    // Any recorded failure belongs to a different file, so this one gets a fresh attempt.
    failedDocumentKeyRef.current = null;
    currentDocumentKeyRef.current = documentKey;
    currentDocumentViewer.addEventListener('documentLoaded', handleDocumentLoaded);

    try {
      markViewerLoadStarted();

      const file = await fetchViewerFileMetadata(client, fileVersion.fileLocation.id);
      if (stopIfStale()) return;

      if (!file) {
        failCurrentLoad();
        return dispatch({
          payload: {
            key: uuid4(),
            message: 'File not found.',
            variant: 'error',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
      }

      const blob = await fetchViewerFileBlob(client, file);
      if (stopIfStale()) return;

      const viewerDocInfo: ViewerDocInfo = {
        documentId,
        fileVersionId: fileVersion.id,
        isSigned,
      };

      await currentDocumentViewer.setDocumentXFDFRetriever(
        createAnnotationRetriever(currentDocumentViewer, getAnnotations)
      );
      if (stopIfStale()) return;

      const loadError = await loadPdfTronDocument(
        currentDocumentViewer,
        blob,
        getViewerFileExtension(file),
        viewerDocInfo
      );
      if (stopIfStale()) return;

      if (loadError) {
        if (!getIsLegacyWordFile(file)) throw loadError;

        Sentry.captureException(loadError);

        const docxBlob = await fetchFilePreviewBlob(documentId, file.id);
        if (stopIfStale()) return;

        const fallbackError = await loadPdfTronDocument(
          currentDocumentViewer,
          docxBlob,
          'docx',
          viewerDocInfo
        );
        if (stopIfStale()) return;
        if (fallbackError) throw fallbackError;
      }

      markViewerLoadFinished();
    } catch (err) {
      if (stopIfStale()) return;

      failCurrentLoad();
      Sentry.captureException(err);
      dispatchErrorNotification();
    }
  }, [
    client,
    dispatch,
    dispatchErrorNotification,
    documentId,
    documentKey,
    documentViewer,
    fileVersion.fileLocation.id,
    fileVersion.id,
    getAnnotations,
    isSigned,
    markViewerDocumentCleared,
    markViewerDocumentLoaded,
    markViewerLoadFinished,
    markViewerLoadStarted,
    scrollNode,
    viewerNode,
  ]);

  const unloadFileViewer = useCallback(
    (toUnload = documentViewer) => {
      if (toUnload) invalidateViewerLoadForDocumentViewer(toUnload);
      currentDocumentKeyRef.current = null;
      failedDocumentKeyRef.current = null;
      if (!toUnload) return;
      toUnload.closeDocument();
      markViewerDocumentCleared();
    },
    [documentViewer, markViewerDocumentCleared]
  );

  useEffect(() => {
    void loadFileViewer();
  }, [loadFileViewer]);

  useEffect(() => {
    if (!documentViewer) return;
    return () => invalidateViewerLoadForDocumentViewer(documentViewer);
  }, [documentKey, documentViewer]);

  return {
    documentViewer,
    scrollTuple: [scrollNode, setScrollNode],
    unloadFileViewer,
    viewerTuple: [viewerNode, setViewerNode],
  };
}

const ViewerDocInfoSchema = z.object({
  documentId: z.string(),
  fileVersionId: z.string(),
  isSigned: z.boolean(),
});
export type ViewerDocInfo = z.infer<typeof ViewerDocInfoSchema>;

export function getViewerDocInfo(documentViewer: Core.DocumentViewer): ViewerDocInfo {
  const viewerDocInfoString = documentViewer.getDocument().getDocumentId();
  return ViewerDocInfoSchema.parse(JSON.parse(viewerDocInfoString));
}

function getViewerDocumentKey(documentId: UUID, fileVersionId: UUID) {
  return `${documentId}:${fileVersionId}`;
}

const viewerLoadTokensByDocumentViewer = new WeakMap<Core.DocumentViewer, number>();

function beginViewerLoadForDocumentViewer(documentViewer: Core.DocumentViewer) {
  const loadId = (viewerLoadTokensByDocumentViewer.get(documentViewer) ?? 0) + 1;
  viewerLoadTokensByDocumentViewer.set(documentViewer, loadId);
  return () => viewerLoadTokensByDocumentViewer.get(documentViewer) === loadId;
}

function invalidateViewerLoadForDocumentViewer(documentViewer: Core.DocumentViewer) {
  viewerLoadTokensByDocumentViewer.set(
    documentViewer,
    (viewerLoadTokensByDocumentViewer.get(documentViewer) ?? 0) + 1
  );
}

async function fetchViewerFileMetadata(client: ReturnType<typeof useApolloClient>, fileId: UUID) {
  const { data } = await client.query<Apollo.GetFileQuery, Apollo.GetFileQueryVariables>({
    query: Apollo.GetFileDocument,
    variables: { fileId },
  });

  return data?.file ?? null;
}

async function fetchViewerFileBlob(
  client: ReturnType<typeof useApolloClient>,
  file: NonNullable<Apollo.GetFileQuery['file']>
) {
  return fetchFreshFilestackBlob({
    refreshUrl: createDocFileUrlRefresher(client, file.id),
    sentryContext: { fileId: file.id, source: 'file-viewer-load' },
    url: file.filestackUrl,
  });
}

function createAnnotationRetriever(
  documentViewer: Core.DocumentViewer,
  getAnnotations: ReturnType<typeof Apollo.useGetAnnotationsLazyQuery>[0]
) {
  // This runs during PDFTron load, so read the active viewer document id at call time.
  return async () => {
    const { fileVersionId } = getViewerDocInfo(documentViewer);
    const { data } = await getAnnotations({ variables: { fileVersionId } });
    return stripPdfOwnWidgetAnnots(data?.annotations?.xfdf);
  };
}

async function loadPdfTronDocument(
  documentViewer: Core.DocumentViewer,
  blob: Blob,
  extension: string,
  viewerDocInfo: ViewerDocInfo
): Promise<unknown> {
  let loadError: unknown = null;

  try {
    await documentViewer.loadDocument(blob, {
      docId: JSON.stringify(viewerDocInfo),
      extension,
      l: pdfTronKey,
      // Gets rid of a selection issue when loading certain DOCX files by converting DOCX -> PDF.
      loadAsPDF: true,
      onError: (error: unknown) => {
        loadError ??= error;
      },
    });
  } catch (error) {
    return loadError ?? error;
  }

  return loadError;
}

async function fetchFilePreviewBlob(documentId: UUID, fileId: UUID): Promise<Blob> {
  const url = new URL(`${getClientPublicOrigin()}${routes.ApiFilePreview}`);
  url.searchParams.set('documentId', documentId);
  url.searchParams.set('fileId', fileId);

  const guestToken = getGuestSessionToken();
  const response = await fetch(url, {
    credentials: 'include',
    ...(guestToken ? { headers: { [guestSessionHeaderName]: guestToken } } : {}),
  });
  if (!response.ok) throw new Error(`File preview failed with HTTP ${response.status}.`);

  return response.blob();
}

function getViewerFileExtension(file: NonNullable<Apollo.GetFileQuery['file']>) {
  return mime.extension(file.mimetype) || 'pdf';
}

function useBindDocumentViewerElements({
  documentViewer,
  scrollNode,
  viewerNode,
}: {
  documentViewer: Core.DocumentViewer | null;
  scrollNode: HTMLDivElement | null;
  viewerNode: HTMLDivElement | null;
}) {
  // Set refs separate from instantiating the viewer so loading the viewer fires no more than once.
  useEffect(() => {
    if (!documentViewer) return;
    if (viewerNode) documentViewer.setViewerElement(viewerNode);
    if (scrollNode) documentViewer.setScrollViewElement(scrollNode);
  }, [documentViewer, scrollNode, viewerNode]);
}

function useRefreshDocumentViewerScrollView(
  documentViewer: Core.DocumentViewer | null,
  width: number
) {
  useEffect(() => {
    if (!documentViewer) return;
    if (document.body.contains(documentViewer.getScrollViewElement()))
      documentViewer.scrollViewUpdated();
  }, [documentViewer, width]);
}

function useClearViewerPanelStateOnClick({
  scrollNode,
  setEditFieldId,
  tabId,
}: {
  scrollNode: HTMLDivElement | null;
  setEditFieldId: (value: string | null) => void;
  tabId: UUID;
}) {
  useEffect(() => {
    if (!scrollNode) return;
    const clearPanelState = () => {
      setEditFieldId(null);
      setVariablePanelStateForTabId(tabId, { focusedField: null });
    };
    scrollNode.addEventListener('click', clearPanelState);

    return () => {
      scrollNode.removeEventListener('click', clearPanelState);
    };
  }, [scrollNode, setEditFieldId, tabId]);
}

function useSyncViewerPageWidthPx(tabId: UUID) {
  const documentViewer = useMaybeDocumentViewer();
  const {
    viewer: { setViewerPageWidthPx, setViewerZoomLevel },
  } = useEditorTabStateActionsForTabId(tabId);
  const docLoadedRef = useRef(false);

  useEffect(() => {
    if (!documentViewer) return;
    const viewer = documentViewer;

    function applyFitZoom() {
      if (!docLoadedRef.current) return;
      const pdfDocument = viewer.getDocument();
      const firstPageInfo = pdfDocument.getPageInfo(1);
      let zoom = viewer.getZoomLevel();
      if (IS_MOBILE) {
        const scrollEl = viewer.getScrollViewElement();
        if (scrollEl && scrollEl.clientWidth > 0) {
          zoom = scrollEl.clientWidth / firstPageInfo.width;
          setViewerZoomLevel(zoom);
        }
      }
      /**
       * Publish the post-fit page width so `useMobileFitZoom` doesn't apply a
       * second CSS transform scale on top of PDFTron's already-fitted canvas.
       */
      setViewerPageWidthPx(firstPageInfo.width * zoom);
    }

    function setPageWidthOnLoad() {
      docLoadedRef.current = true;
      applyFitZoom();
    }
    function setPageWidthOnUnload() {
      docLoadedRef.current = false;
      setViewerPageWidthPx(0);
    }

    documentViewer.addEventListener('documentLoaded', setPageWidthOnLoad);
    documentViewer.addEventListener('documentUnloaded', setPageWidthOnUnload);

    applyFitZoom();

    return () => {
      documentViewer.removeEventListener('documentLoaded', setPageWidthOnLoad);
      documentViewer.removeEventListener('documentUnloaded', setPageWidthOnUnload);
    };
  }, [documentViewer, setViewerPageWidthPx, setViewerZoomLevel]);
}
